import { FC, useMemo } from "react";

import { useTheme } from "@mui/material";

import Image from "@/components/Image/Image";
import { getYoutubeIdentifier } from "@/features/FormV2/helpers/getYoutubeIdentifier";
import { GeneratingBannerPlaceholder } from "@/features/FormV2/Step1/GeneratingBannerPlaceholder";

export type ThemedFormBannerProps = { imageSrc: string } | { videoSrc: string };

export const ThemedFormBanner: FC<ThemedFormBannerProps> = (props) => {
  if ("videoSrc" in props) {
    return <ThemedFormVideoBanner src={props.videoSrc} />;
  }

  if ("imageSrc" in props) {
    if (props.imageSrc === IS_GENERATING_BANNER_SRC) {
      return <GeneratingBannerPlaceholder />;
    }

    return <ThemedFormImageBanner src={props.imageSrc} />;
  }

  // Should never happen due to TypeScript's exhaustive checking
  return null;
};

type ThemedFormVideoBannerProps = {
  src: string;
};

const ThemedFormVideoBanner: FC<ThemedFormVideoBannerProps> = ({ src }) => {
  const theme = useTheme();
  const youtubeIdentifier = useMemo(() => getYoutubeIdentifier(src), [src]);

  const styles = {
    width: "100%",
    aspectRatio: "16 / 9",
    lineHeight: 0,
    borderRadius: theme.radius(1),
  };

  return (
    <iframe
      src={`https://www.youtube.com/embed/${youtubeIdentifier}?autoplay=1&mute=1&loop=1;showinfo=0`}
      allowFullScreen
      frameBorder="0"
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture;"
      data-chromatic="ignore"
      style={styles}
    />
  );
};

type ThemedFormImageBannerProps = {
  src: string;
  onLoadingComplete?: () => void;
};

const ThemedFormImageBanner: FC<ThemedFormImageBannerProps> = ({ src, onLoadingComplete, ...rest }) => {
  const theme = useTheme();

  const styles = {
    backgroundColor: theme.palette.forms.surface.quiet,
    aspectRatio: "16 / 9",
    objectFit: "cover",
    borderRadius: theme.radius(1),
  };

  return (
    <Image
      src={src}
      alt=""
      resolution={1200}
      priority
      onLoadingComplete={onLoadingComplete}
      data-test="banner-image"
      width="100%"
      height="auto"
      sx={styles}
      {...rest}
    />
  );
};

export const IS_GENERATING_BANNER_SRC = "is-generating-banner-src";
